'use strict'

import { useAuthStore } from '@store/Auth'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import moment from 'moment-timezone'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

// walrus
import useCopy from './use-copy'
import MemberQueueBar from './member-queue-bar'
import { SHOW_QUEUE_BAR } from '../../queue-experiment'
import useLoadingState from '../../use-loading-state'
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Circle from '@components/kit/circle'
import DateTimePicker, {
  ImperativeDateTimePickerProps
} from '@components/kit/datetime-picker'
import Pressable, { PressableProps } from '@components/kit/pressable'
import Text from '@components/kit/text'

import { ageFromDOB, offsetToUsaTimezoneMap } from '@utils/date'
import { parsePhoneNumber } from '@utils/phone'
import { languageDictionary } from '@utils/language-parser'
import { titleCase } from '@utils/string-formatters'
import { BoxLink } from '@components/kit/link'
import Loading from '@components/kit/loading'
import { Column, Row } from '@components/kit/grid'
import {
  CopyIcon,
  MessageIcon,
  CheckMarkVioletIcon,
  CloseIcon,
  AvatarPharmacistFilledIcon,
  DiamondStackIcon,
  MedicationsIcon,
  ProfileFilledIcon
} from '@components/kit/icons'
import Modal from '@components/kit/modal'
import Button from '@components/kit/button'

// lib
import Bugsnag from '@lib/bugsnag'
import { ChatContext } from '../../chat.web'
import MemberConditionsPopover from './member-conditions-popover'
import MemberInfoPopper from './member-info-popper'
import MemberAllergyPopover from './member-allergy-popover'
import MemberAddInfoPopover, {
  MemberAddEmailModal,
  MemberAddPhoneModal
} from './member-add-popover'
import {
  ColumnWrapper,
  HorizontalRule,
  LabelText,
  RowWrapper,
  ValueText
} from '@components/table'
import { useAllergyStore } from '@store/Allergy'
import { useDisabilityStore } from '@store/Disability'
import { useAddressStore } from '@store/Address'
import { useFollowUpsStore } from '@store/FollowUps'
import { useUserStore } from '@store/User'
import { useNeedsAttentionStore } from '@store/NeedsAttention'
import { useConditionsStore } from '@store/Conditions'
import { useIncentiveStore } from '@store/Incentive'
import { IUserEngagements } from '@store/Incentive/types'
import { IFollowUp } from '@store/FollowUps/types'
import { DisabilityDetails } from '@store/Disability/types'
import { IAvailableCondition, IUserCondition } from '@store/Conditions/types'
import { IAllergy } from '@store/Allergy/types'
import { IUserAddress } from '@store/Address/types'
import { IUserSettings } from '@store/User/types'
import { colors } from '@theme'
import { ChatSuggestions } from './chat-suggestions'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

const Gender = {
  F: 'Female',
  M: 'Male',
  0: 'Not Disclosed'
}

/* -----------------------------------------------------------------------------
 * CheckMarkText
 * -------------------------------------------------------------------------- */

const CheckMarkText = () => (
  <Box>
    <Box>
      <ValueText>{'\u26AB'}</ValueText>
    </Box>
    <Box position='absolute' top={1} left={1}>
      <Text textColor={'white'} font='display.12.bold'>
        {'\u2713'}
      </Text>
    </Box>
  </Box>
)

/* -----------------------------------------------------------------------------
 * ConditionItem
 * -------------------------------------------------------------------------- */

const ConditionItem = ({
  condition,
  index,
  userId
}: {
  condition: IUserCondition
  index: number
  userId: number
}) => (
  <>
    <Column
      key={index}
      flex='0 1 50%'
      maxWidth={'50%'}
      marginLeft={index % 2 ? '16' : '00'}
    >
      <ValueText>{condition.name}</ValueText>
    </Column>
    <Column flexDirection='row' width='120px' justifyContent='right'>
      {!condition?.origin_user_id && (
        <DiamondStackIcon width={24} height={24} marginX='08' />
      )}
      {(condition.origin_user_id === userId || condition.confirmed) && (
        <ProfileFilledIcon width={24} height={24} marginX='08' />
      )}
      {!!condition?.origin_user_id && condition.origin_user_id !== userId && (
        <AvatarPharmacistFilledIcon width={24} height={24} marginX='08' />
      )}
    </Column>
  </>
)

/* -----------------------------------------------------------------------------
 * AllergyItem
 * -------------------------------------------------------------------------- */

const AllergyItem = ({
  allergy,
  index
}: {
  allergy: IAllergy
  index: number
}) => (
  <Column flex='0 1 50%' maxWidth={'50%'} marginLeft={index % 2 ? '16' : '00'}>
    <ValueText>
      {allergy.drugName}
      {allergy.drugForm && ` (${allergy.drugForm})`}
    </ValueText>
    {allergy.description && <LabelText>{allergy.description}</LabelText>}
  </Column>
)

/* -----------------------------------------------------------------------------
 * EligibilityBadge
 *
 * The pill @walrus/common's Badge drew here, its only use.
 * -------------------------------------------------------------------------- */

const EligibilityBadge = ({ eligible }: { eligible: boolean }) => (
  <Box
    bg={eligible ? 'lightGreen' : 'shade03'}
    borderRadius='small'
    ml='08'
    marginTop={'-02'}
    px='08'
    py='04'
  >
    <Text
      textAlign='center'
      variant='badge'
      textColor={eligible ? 'darkGreen' : 'shade06'}
    >
      {eligible ? 'Eligible' : 'Not eligible'}
    </Text>
  </Box>
)

/* -----------------------------------------------------------------------------
 * CopyButton
 * -------------------------------------------------------------------------- */

const CopyButton = ({ onPress, ...otherProps }: PressableProps) => (
  <Pressable ml='12' onPress={onPress} {...otherProps}>
    <Circle size={39} backgroundColor='paleViolet'>
      <CopyIcon fill='violet' />
    </Circle>
  </Pressable>
)

/* -----------------------------------------------------------------------------
 * MemberInfo
 * -------------------------------------------------------------------------- */

type MemberInfoProps = {
  openEvents: () => void
}

const MemberInfo = ({ openEvents }: MemberInfoProps) => {
  const { userId, roomId, userInfo, refreshUserInfo, setMemberTimezone } =
    useContext(ChatContext)
  // the chat screen fetches this once and shares it; the four consumers that
  // each requested it on their own made five calls per member
  const user = userInfo
  const professionalId = useAuthStore(state => state.userId)
  const {
    getUserProfile,
    getUserSettings,
    rematchMember,
    removeMemberNeedsAttention
  } = useUserStore.getState()
  const [phonesAndEmails, setPhonesAndEmails] = useState<
    Record<string, { phone?: string; email?: string }>
  >({})
  const {
    getMemberScheduledFollowUp,
    scheduleMemberFollowUp,
    updateFollowUpStatus
  } = useFollowUpsStore.getState()
  const dateTimePickerRef = useRef<ImperativeDateTimePickerProps>(null)
  const [followUpAnchor, setFollowUpAnchor] = useState<HTMLElement | null>(null)
  const [memberFollowUp, setMemberFollowUp] = useState<IFollowUp | null>(null)
  const [isUpdatingFollowUp, setIsUpdatingFollowUp] = useState<boolean>(false)
  const [isRematchLoading, setIsRematchLoading] = useState<boolean>(false)
  const [isNeedsAttentionLoading, setIsNeedsAttentionLoading] =
    useState<boolean>(false)

  const [userSettings, setUserSettings] = useState<IUserSettings | null>(null)
  const [isUserSettingsLoading, setIsUserSettingsLoading] =
    useState<boolean>(true)

  useEffect(() => {
    setIsUserSettingsLoading(true)
    getUserSettings(userId).then(settings => {
      setUserSettings(settings)
      setIsUserSettingsLoading(false)
    })
  }, [getUserSettings, userId])

  const [userAllergies, setUserAllergies] = useState<IAllergy[]>([])
  const [isAllergiesLoading, setIsAllergiesLoading] = useState<boolean>(true)
  const { getUserAllergies } = useAllergyStore.getState()

  const refetchAllergies = () => {
    getUserAllergies(userId).then(allergies => {
      setUserAllergies(allergies)
    })
  }

  const [userAddress, setUserAddress] = useState<IUserAddress | null>(null)
  const { getUserAddress } = useAddressStore.getState()

  useEffect(() => {
    const fetchAddress = async () => {
      const userAddress = await getUserAddress(userId)
      if (userAddress) {
        setUserAddress(userAddress)
      }
    }
    fetchAddress()
  }, [getUserAddress, userId])

  const { getUserConditions, getAvailableConditions } =
    useConditionsStore.getState()
  const [userConditions, setUserConditions] = useState<IUserCondition[]>([])
  const [isUserConditionsLoading, setIsUserConditionsLoading] =
    useState<boolean>(true)
  const [conditionsAvailable, setConditionsAvailable] = useState<
    IAvailableCondition[]
  >([])
  const [isConditionsAvailableLoading, setIsConditionsAvailableLoading] =
    useState<boolean>(true)

  const loadUserConditions = useCallback(async () => {
    setIsUserConditionsLoading(true)
    setUserConditions(await getUserConditions(userId))
    setIsUserConditionsLoading(false)
  }, [getUserConditions, userId])

  useEffect(() => {
    loadUserConditions()
  }, [loadUserConditions])

  useEffect(() => {
    setIsConditionsAvailableLoading(true)
    getAvailableConditions(userId).then(conditions => {
      setConditionsAvailable(conditions)
      setIsConditionsAvailableLoading(false)
    })
  }, [getAvailableConditions, userId])

  // MAIN contacts come from /info; /profile only supplies the secondary ones
  const loadContacts = async () => {
    const info = user
    const profile = await getUserProfile(userId)

    const grouped: Record<string, { phone?: string; email?: string }> = {}
    const put = (label: string, key: 'phone' | 'email', value?: string) => {
      if (!value) {
        return
      }
      grouped[label] = { ...grouped[label], [key]: value }
    }

    // /profile repeats the MAIN entry and leaves secondary labels null, so the
    // main value is filtered out and unlabeled ones get a positional key
    const addSecondary = (
      entries: { label: string | null; value?: string }[],
      key: 'phone' | 'email',
      mainValue?: string,
      format?: (value: string) => string
    ) => {
      let unlabeled = 0
      entries.forEach(entry => {
        const value = entry.value
        if (!value || value === mainValue) {
          return
        }
        const label = entry.label?.trim().toLowerCase()
        if (!label) {
          unlabeled += 1
        }
        put(
          label || `other${unlabeled > 1 ? ` ${unlabeled}` : ''}`,
          key,
          format ? format(value) : value
        )
      })
    }

    put(
      'main',
      'phone',
      info?.contacts?.phone
        ? parsePhoneNumber(`${info.contacts.phone}`) || info.contacts.phone
        : undefined
    )
    put('main', 'email', info?.contacts?.email)

    addSecondary(
      (profile?.phones ?? []).map(e => ({ label: e.label, value: e.phone })),
      'phone',
      info?.contacts?.phone,
      value => parsePhoneNumber(`${value}`) || value
    )
    addSecondary(
      (profile?.emails ?? []).map(e => ({ label: e.label, value: e.email })),
      'email',
      info?.contacts?.email
    )

    setPhonesAndEmails(grouped)
  }

  const updatePhonesLocally = (label: string, value: string) => {
    setPhonesAndEmails(current => ({
      ...current,
      [label]: { ...current[label], phone: `+1 ${value}` }
    }))
  }

  const updateEmailsLocally = (label: string, value: string) => {
    setPhonesAndEmails(current => ({
      ...current,
      [label]: { ...current[label], email: value }
    }))
  }

  const [isAddEmailModalOpen, setAddEmailModalOpen] = useState<boolean>(false)
  const [isAddPhonelModalOpen, setAddPhonelModalOpen] = useState<boolean>(false)

  const [onCopyGeneratedId] = useCopy(user?.generated ?? '')
  const [onCopyWalrusId] = useCopy(userId.toString())

  const isAssigned = user?.professional?.id === professionalId

  const [isAssignModalOpen, setAssignModalOpen] = useState<boolean>(false)

  const { getUserDisabilityDetails } = useDisabilityStore.getState()
  const [disabilityDetails, setDisabilityDetails] =
    useState<DisabilityDetails | null>(null)
  const [isDisabilityLoading, setIsDisabilityLoading] = useState<boolean>(true)

  const openAssignModal = () => {
    setAssignModalOpen(true)
  }

  const closeAssignModal = () => {
    setAssignModalOpen(false)
  }

  const openDateTimePicker = () => {
    dateTimePickerRef.current.present()
  }

  const saveFollowUp = async (date: moment.Moment) => {
    const followUp = await scheduleMemberFollowUp(
      userId,
      date.utc().format('YYYY-MM-DDTHH:mm:ss')
    )
    setMemberFollowUp(followUp)
  }

  const setFollowUpStatus = async (status: 'DONE' | 'UNREALIZED') => {
    setIsUpdatingFollowUp(true)
    const followUp = await updateFollowUpStatus(memberFollowUp.id, status)
    // the member only keeps a scheduled follow up; resolving it clears the card
    setMemberFollowUp(followUp?.status === 'SCHEDULED' ? followUp : null)
    setIsUpdatingFollowUp(false)
  }

  const handleAssigToMe = async () => {
    setIsRematchLoading(true)
    const rematched = await rematchMember(professionalId, userId)
    if (rematched) {
      await refreshUserInfo()
      // a member who needs attention joins the professional's queue
      useNeedsAttentionStore.getState().loadNextMember(userId, professionalId)
    } else {
      Bugsnag.notify(
        `Error: Failed to rematch user. (memberId ${userId}, professionalId ${professionalId}`
      )
    }
    setIsRematchLoading(false)
    closeAssignModal()
  }

  const handleNeedsAttention = async () => {
    setIsNeedsAttentionLoading(true)
    const response = await removeMemberNeedsAttention(userId)
    if (response) {
      await refreshUserInfo()
      // the queue without this member, and the chat's queue tab opens for a
      // moment to offer the next one. Flipper's next_user_id is not used: it
      // does not know which members were skipped
      await useNeedsAttentionStore
        .getState()
        .loadNextMember(userId, professionalId, { reveal: true })
    } else {
      Bugsnag.notify(
        `Error: Failed to mark as resolved user. (memberId ${userId}, professionalId ${professionalId}`
      )
    }
    setIsNeedsAttentionLoading(false)
  }

  const { isLoaded, isReloading } = useLoadingState(async () => {
    await Promise.all([
      loadContacts(),
      getMemberScheduledFollowUp(userId).then(setMemberFollowUp)
    ])
  }, [userId])

  const { getUserEngagements } = useIncentiveStore.getState()
  const [planData, setPlanData] = useState<IUserEngagements | null>(null)
  const [isPlanDataLoading, setIsPlanDataLoading] = useState<boolean>(true)

  useEffect(() => {
    setIsPlanDataLoading(true)
    getUserEngagements(userId).then(engagements => {
      setPlanData(engagements)
      setIsPlanDataLoading(false)
    })
  }, [getUserEngagements, userId])

  useEffect(() => {
    if (isAllergiesLoading) {
      getUserAllergies(userId).then(allergies => {
        setUserAllergies(allergies)
        setIsAllergiesLoading(false)
      })
    }
  }, [isAllergiesLoading, userId])

  useEffect(() => {
    if (isDisabilityLoading) {
      getUserDisabilityDetails(userId).then(details => {
        setDisabilityDetails(details)
        setIsDisabilityLoading(false)
      })
    }
  }, [isDisabilityLoading, userId])

  useEffect(() => {
    setIsDisabilityLoading(true)
    setIsAllergiesLoading(true)
  }, [userId])

  useEffect(() => {
    const zone = userSettings?.timezone
      ? offsetToUsaTimezoneMap[userSettings.timezone]
      : 'UTC'
    moment.tz.setDefault(zone)
    // setDefault redraws nothing: without this, times drawn before the
    // settings arrived kept the previous zone
    setMemberTimezone(zone ?? null)
  }, [userSettings?.timezone])

  return (
    <React.Fragment>
      <Box
        flexDirection='row'
        alignItems='flex-start'
        mb={{ desktop: '00', mobile: '24' }}
      >
        {userId ? (
          <Pressable
            display={{ desktop: 'none' }}
            mr='-04'
            p='04'
            onPress={openEvents}
          >
            <MedicationsIcon width='32' height='32' fill='violet' />
          </Pressable>
        ) : null}
        {userId ? (
          <BoxLink
            display={{ desktop: 'none' }}
            mr='-04'
            p='04'
            path={`/chat/${userId}/${roomId}`}
          >
            <MessageIcon width='32' height='32' fill='violet' />
          </BoxLink>
        ) : null}
      </Box>

      {SHOW_QUEUE_BAR && userId ? <MemberQueueBar userId={userId} /> : null}

      {user?.needs_attention ? (
        <Card
          height={65}
          width='100%'
          mb='24'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <LabelText>Mark as resolved</LabelText>
          <Box flexDirection='row' alignItems='center'>
            {isNeedsAttentionLoading ? (
              <Loading mr='24' />
            ) : (
              <Pressable mr='08' onPress={handleNeedsAttention}>
                <Circle size={39} backgroundColor='paleViolet'>
                  <CheckMarkVioletIcon width={13} height={9} />
                </Circle>
              </Pressable>
            )}
          </Box>
        </Card>
      ) : null}

      {memberFollowUp ? (
        <Card
          height={65}
          width='100%'
          mb='24'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <LabelText>Follow-up Date</LabelText>
          {isUpdatingFollowUp ? (
            <Loading mr='24' />
          ) : (
            <Box flexDirection='row' alignItems='center'>
              <Text textColor='purple' mr='12'>
                {moment.utc(memberFollowUp.scheduled_date).format('MM/DD/YYYY')}
              </Text>
              <Pressable mr='08' onPress={() => setFollowUpStatus('DONE')}>
                <Circle size={39} backgroundColor='paleViolet'>
                  <CheckMarkVioletIcon width={13} height={9} />
                </Circle>
              </Pressable>
              <Pressable onPress={() => setFollowUpStatus('UNREALIZED')}>
                <Circle size={39} backgroundColor='paleViolet'>
                  <CloseIcon fill='violet' width={13} height={13} />
                </Circle>
              </Pressable>
            </Box>
          )}
        </Card>
      ) : null}

      <ChatSuggestions />

      <Row mt='00'>
        <Text ml='12' font='display.16.bold'>
          Personal
        </Text>
      </Row>

      <Box mt='08' flexDirection='row' gap='04' flexWrap='wrap'>
        {!memberFollowUp && isLoaded && !isReloading ? (
          <>
            <Button
              ref={setFollowUpAnchor}
              onPress={openDateTimePicker}
              size='fit'
              variant='tertiary'
            >
              Add follow up date
            </Button>
            {/* open once the button exists: the picker shows itself when presented */}
            <MemberInfoPopper open={!!followUpAnchor} anchorEl={followUpAnchor}>
              <DateTimePicker
                onSubmit={saveFollowUp}
                ref={dateTimePickerRef}
                minDate={moment()}
              />
            </MemberInfoPopper>
          </>
        ) : null}
        {isAssigned ? null : (
          <Button onPress={openAssignModal} size='fit' variant='tertiary'>
            Assign to me
          </Button>
        )}
        <MemberAddInfoPopover
          onAddEmail={() => setAddEmailModalOpen(true)}
          onAddPhone={() => setAddPhonelModalOpen(true)}
        />
        {!isConditionsAvailableLoading &&
          !isUserConditionsLoading &&
          !!conditionsAvailable?.length && (
            <MemberConditionsPopover
              conditionsAvailable={conditionsAvailable}
              selectedConditions={userConditions}
              refetchUserConditions={loadUserConditions}
            />
          )}
        <MemberAllergyPopover
          allergies={userAllergies}
          onAllergiesChanged={refetchAllergies}
        />
      </Box>

      {isLoaded && !isPlanDataLoading && !isReloading ? (
        <Card mt='08' px='00' py='00'>
          {user?.preferred_name ? (
            <Row width='100%'>
              <ColumnWrapper>
                <LabelText>Preferred Name</LabelText>
                <ValueText>{user.preferred_name}</ValueText>
              </ColumnWrapper>
            </Row>
          ) : null}
          <Row width={'100%'}>
            <ColumnWrapper>
              <LabelText>Full Name</LabelText>
              <ValueText>
                {user ? `${user.first_name} ${user.last_name}` : ''}
              </ValueText>
            </ColumnWrapper>
          </Row>

          <Row width='100%'>
            <ColumnWrapper maxWidth={'20%'}>
              <LabelText>Gender</LabelText>
              <ValueText>{user?.gender ? Gender[user.gender] : ''}</ValueText>
            </ColumnWrapper>
            <ColumnWrapper maxWidth={'40%'}>
              <LabelText>Age</LabelText>
              <ValueText>
                {user?.dob
                  ? `${ageFromDOB(user.dob)} (${moment(user.dob).format(
                      'MM/DD/YYYY'
                    )})`
                  : ''}
              </ValueText>
            </ColumnWrapper>
            <ColumnWrapper width='40%'>
              <LabelText>Disability</LabelText>
              <ValueText
                textColor={
                  disabilityDetails?.has_disability
                    ? 'walrusGreen500'
                    : 'newPurple'
                }
              >
                {disabilityDetails?.has_disability ? 'Yes' : 'No'}
              </ValueText>
            </ColumnWrapper>
          </Row>
          <Row width={'100%'}>
            <ColumnWrapper flex='0 1 auto'>
              <Box flexDirection={'row'}>
                <LabelText>Member UUID</LabelText>
                <EligibilityBadge eligible={!user?.inactive} />
              </Box>
              <ValueText truncate>{user?.generated}</ValueText>
            </ColumnWrapper>
            <ColumnWrapper flex='0 1 auto' marginRight='16'>
              <CopyButton onPress={onCopyGeneratedId} />
            </ColumnWrapper>
            <ColumnWrapper flex='0 1 50%'>
              <LabelText>Walrus ID</LabelText>
              <ValueText>{userId}</ValueText>
            </ColumnWrapper>
            <ColumnWrapper flex='0 1 auto' marginRight='16'>
              <CopyButton onPress={onCopyWalrusId} />
            </ColumnWrapper>
          </Row>

          <HorizontalRule />

          {!isUserConditionsLoading && !!userConditions?.length && (
            <>
              <RowWrapper>
                <LabelText>Conditions</LabelText>
              </RowWrapper>
              {userConditions?.map((condition, index) => {
                if (index % 2) return null
                return (
                  <RowWrapper width={'100%'} marginRight='08'>
                    <ConditionItem
                      condition={condition}
                      index={index}
                      userId={userId}
                    />
                    {userConditions[index + 1] ? (
                      <ConditionItem
                        condition={userConditions[index + 1]}
                        index={index + 1}
                        userId={userId}
                      />
                    ) : (
                      <Column width='50%' marginLeft='08' />
                    )}
                  </RowWrapper>
                )
              })}
              <HorizontalRule />
            </>
          )}

          {!isAllergiesLoading && !!userAllergies?.length && (
            <>
              <RowWrapper>
                <LabelText>Allergies</LabelText>
              </RowWrapper>
              {userAllergies.map((allergy, index) => {
                if (index % 2) return null
                return (
                  <RowWrapper width={'100%'} marginRight='08'>
                    <AllergyItem allergy={allergy} index={index} />
                    {userAllergies[index + 1] ? (
                      <AllergyItem
                        allergy={userAllergies[index + 1]}
                        index={index + 1}
                      />
                    ) : (
                      <Column width='50%' marginLeft='08' />
                    )}
                  </RowWrapper>
                )
              })}
              <HorizontalRule />
            </>
          )}

          <Row width='100%'>
            <ColumnWrapper width='70%'>
              <LabelText>Program</LabelText>
              <ValueText> </ValueText>
            </ColumnWrapper>
            <ColumnWrapper width='30%'>
              <LabelText>Total cost of care</LabelText>
              <ValueText> </ValueText>
            </ColumnWrapper>
          </Row>

          <Row width='100%'>
            <ColumnWrapper width='70%'>
              <LabelText>Employer</LabelText>
              <ValueText>{user?.company?.name ?? ' '}</ValueText>
            </ColumnWrapper>
            <ColumnWrapper width='30%'>
              <LabelText>Job Title</LabelText>
              <ValueText> </ValueText>
            </ColumnWrapper>
          </Row>

          <Row width='100%'>
            <ColumnWrapper width='70%'>
              <LabelText>Professional</LabelText>
              <ValueText>{user?.professional?.display_name ?? ''}</ValueText>
            </ColumnWrapper>
            <ColumnWrapper width='30%'>
              <LabelText>Preferred language</LabelText>
              <ValueText>
                {!isUserSettingsLoading && userSettings?.chatLanguage
                  ? languageDictionary[userSettings.chatLanguage]
                  : 'Original (English)'}
              </ValueText>
            </ColumnWrapper>
          </Row>

          <HorizontalRule />

          {Object.keys(phonesAndEmails).map((label, index) => (
            <Row key={index} width='100%'>
              <ColumnWrapper width='70%'>
                <LabelText>Email ({titleCase(label)})</LabelText>
                {phonesAndEmails[label].email && (
                  <a
                    href={`mailto:${phonesAndEmails[label].email}`}
                    style={{
                      color: colors.newPurple,
                      textDecorationLine: 'none',
                      width: 'fit-content'
                    }}
                  >
                    <ValueText>{phonesAndEmails[label].email}</ValueText>
                  </a>
                )}
              </ColumnWrapper>
              <ColumnWrapper width='30%'>
                <LabelText>Phone ({titleCase(label)})</LabelText>
                {phonesAndEmails[label].phone && (
                  <a
                    href={`callto:${phonesAndEmails[label].phone}`.replace(
                      /[\s-]/g,
                      ''
                    )}
                    style={{
                      color: colors.newPurple,
                      width: 'fit-content'
                    }}
                  >
                    <ValueText>{phonesAndEmails[label].phone}</ValueText>
                  </a>
                )}
              </ColumnWrapper>
            </Row>
          ))}

          {isLoaded && user && (
            <Row width='100%'>
              <ColumnWrapper width='40%'>
                <LabelText>Push notification</LabelText>
                <ValueText>{user?.device?.push_enabled || '-'}</ValueText>
              </ColumnWrapper>
              <ColumnWrapper width='30%'>
                <LabelText>Enabled date</LabelText>
                <ValueText>
                  {user?.device?.push_activated_date
                    ? moment
                        .utc(user.device.push_activated_date)
                        .format('MM/DD/YYYY')
                    : '-'}
                </ValueText>
              </ColumnWrapper>
              <ColumnWrapper width='30%'>
                <LabelText>Last verified at</LabelText>
                <ValueText>
                  {user?.device?.last_activity
                    ? moment.utc(user.device.last_activity).format('MM/DD/YYYY')
                    : '-'}
                </ValueText>
              </ColumnWrapper>
            </Row>
          )}

          <Row width='100%'>
            <ColumnWrapper width='40%'>
              <LabelText>App Usage</LabelText>
              {user?.device?.last_activity ? (
                <Box flexDirection='row' alignItems='left'>
                  <CheckMarkText />
                  <ValueText>
                    {' ' +
                      moment
                        .utc(user?.device?.last_activity)
                        .format('MM/DD/YYYY - h:mm A')}
                  </ValueText>
                </Box>
              ) : (
                <ValueText> </ValueText>
              )}
            </ColumnWrapper>
            <ColumnWrapper width='30%'>
              <LabelText>Enrolled in incentive</LabelText>
              {planData?.plan.start_date ? (
                <Box flexDirection='row' alignItems='left'>
                  <CheckMarkText />
                  <ValueText>
                    {' ' +
                      moment.utc(planData.plan.start_date).format('MMMM YYYY')}
                  </ValueText>
                </Box>
              ) : (
                <ValueText>Not enrolled</ValueText>
              )}
            </ColumnWrapper>
            <ColumnWrapper width='30%'>
              <LabelText>Current Time</LabelText>
              {!isUserSettingsLoading && userSettings?.timezone ? (
                <ValueText textColor='green'>
                  {moment()
                    .utc()
                    .utcOffset(userSettings.timezone * 60) // Converta horas para minutos
                    .format('h:mm A')}
                </ValueText>
              ) : (
                <ValueText> </ValueText>
              )}
            </ColumnWrapper>
          </Row>

          {isLoaded && (
            <Row width='100%'>
              <ColumnWrapper width='100%%'>
                <LabelText>App Version</LabelText>
                <ValueText>{user?.device?.app_version || '-'}</ValueText>
              </ColumnWrapper>
            </Row>
          )}

          {userAddress ? (
            <React.Fragment>
              <HorizontalRule />
              <Row>
                <ColumnWrapper>
                  <LabelText>Street Address</LabelText>
                  <ValueText>{userAddress?.line || ''}</ValueText>
                </ColumnWrapper>
              </Row>
              <Row flexWrap='wrap'>
                <ColumnWrapper width='40%'>
                  <LabelText>City</LabelText>
                  <ValueText>{userAddress?.city || ''}</ValueText>
                </ColumnWrapper>

                <ColumnWrapper width='30%'>
                  <LabelText>State</LabelText>
                  <ValueText>{userAddress?.state || ''}</ValueText>
                </ColumnWrapper>

                <ColumnWrapper width='30%'>
                  <LabelText>Zipcode</LabelText>
                  <ValueText>{userAddress?.zipcode || ''}</ValueText>
                </ColumnWrapper>
              </Row>
            </React.Fragment>
          ) : null}
        </Card>
      ) : (
        <Card mt='20' width='100%' height={333} />
      )}

      <MemberAddEmailModal
        open={isAddEmailModalOpen}
        onRequestClose={() => setAddEmailModalOpen(false)}
        updateEmailsLocally={updateEmailsLocally}
      />

      <MemberAddPhoneModal
        open={isAddPhonelModalOpen}
        onRequestClose={() => setAddPhonelModalOpen(false)}
        updatePhonesLocally={updatePhonesLocally}
      />

      <Modal
        open={isAssignModalOpen}
        onRequestClose={closeAssignModal}
        showCloseButton={false}
        maxWidth={411}
        borderRadius='medium'
      >
        <Text variant='h3' mb='20'>
          Are you sure you want to assign this member to you?
        </Text>

        <Box width='100%' flexDirection='row' justifyContent='center'>
          <Button
            onPress={closeAssignModal}
            variant='punchThroughViolet'
            borderColor='shade03'
            borderWidth={1}
          >
            Cancel
          </Button>
          <Button onPress={handleAssigToMe} width={75} height={52} ml='12'>
            {isRematchLoading ? <Loading fill='white' size='small' /> : 'yes'}
          </Button>
        </Box>
      </Modal>
    </React.Fragment>
  )
}

export default MemberInfo
